"use client";

import type * as React from "react";
import { useEffect, useState } from "react";
import { ChevronDownIcon, LucideMapPin, LucideMapPinCheck, LucideMapPinPlus, LucideUndo2 } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { BookingLocationDetail } from "@/lib/booking/locations";
import { useBookingLocations } from "@/lib/hooks/use-booking-locations";
import { cn } from "@/lib/utils";

export const bookingLocationTriggerClassName =
  "h-12 min-h-12 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-base font-normal shadow-none focus-visible:border-gray-300 focus-visible:ring-brand data-[size=default]:h-12 data-[size=sm]:h-12";

type BookingBranchLocationPickerProps = {
  value: string;
  onValueChange: (value: string) => void;
  id?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  mode: "pickup" | "return";
  pickupLocation?: string;
  onReturnAtPickup?: () => void;
  placeholder?: string;
  emptyDetailMessage?: string;
  className?: string;
};

export function BookingBranchLocationPicker({
  value,
  onValueChange,
  id,
  open: controlledOpen,
  onOpenChange,
  mode,
  pickupLocation = "",
  onReturnAtPickup,
  placeholder = "Choose branch...",
  emptyDetailMessage = "Select a branch to see details.",
  className,
}: BookingBranchLocationPickerProps) {
  const {
    locations,
    isLoading,
    isReady,
    error,
    getLocationLabel,
    getLocationDetail,
  } = useBookingLocations();
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlledOpen = controlledOpen !== undefined;
  const open = isControlledOpen ? controlledOpen : internalOpen;

  const setOpen = (next: boolean) => {
    if (!isControlledOpen) {
      setInternalOpen(next);
    }
    onOpenChange?.(next);
  };

  const [previewValue, setPreviewValue] = useState(
    value || (mode === "return" ? pickupLocation : "") || "",
  );

  useEffect(() => {
    if (open) {
      setPreviewValue(value || (mode === "return" ? pickupLocation : "") || "");
    }
  }, [open, value, pickupLocation, mode]);

  useEffect(() => {
    if (value) {
      setPreviewValue(value);
    }
  }, [value]);

  const displayValue =
    previewValue || value || (mode === "return" ? pickupLocation : "");
  const detail = displayValue ? getLocationDetail(displayValue) : null;
  const displayLabel = displayValue ? getLocationLabel(displayValue) : "";

  const handleSelect = (locationValue: string) => {
    onValueChange(locationValue);
    setPreviewValue(locationValue);
    setOpen(false);
  };

  const handleReturnAtPickup = () => {
    onReturnAtPickup?.();
    setOpen(false);
  };

  if (isLoading) {
    return (
      <div
        id={id}
        className={cn(
          bookingLocationTriggerClassName,
          "flex items-center text-gray-400",
          className,
        )}
      >
        Loading locations…
      </div>
    );
  }

  if (!isReady) {
    return (
      <div
        id={id}
        className={cn(
          bookingLocationTriggerClassName,
          "flex items-center text-destructive",
          className,
        )}
      >
        {error ?? "No locations available"}
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        id={id}
        className={cn(
          bookingLocationTriggerClassName,
          "flex w-full cursor-pointer items-center justify-between gap-2 text-left data-[selected=true]:bg-amber-300",
          className,
        )}
      >
        <span className="flex min-w-0 flex-1 items-center gap-2">
          <i className="bi bi-search shrink-0 text-gray-400" aria-hidden="true" />
          <span className={cn("truncate", !value && "text-gray-400")}>
            {value ? getLocationLabel(value) : placeholder}
          </span>
        </span>
        <ChevronDownIcon className="size-4 shrink-0 text-gray-400" />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-[min(720px,calc(100vw-3rem))] overflow-hidden rounded-xl p-0 shadow-lg"
      >
        <div className="flex max-h-[min(420px,70vh)]">
          <div className="flex w-[min(280px,45%)] shrink-0 flex-col overflow-y-auto border-r border-gray-100">
            {mode === "return" ? (
              <>
                <button
                  type="button"
                  onClick={handleReturnAtPickup}
                  className="flex w-full cursor-pointer items-center gap-3 border-b border-gray-100 px-4 py-3 text-left text-sm font-medium text-gray-900 transition-colors hover:bg-gray-50"
                >
                  <LucideUndo2 size={16}/>
                  Return at pickup
                </button>

                {pickupLocation ? (
                  <div className="px-2 py-3">
                    <p className="px-2 pb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                      Pickup branch
                    </p>
                    <button
                      type="button"
                      onClick={() => handleSelect(pickupLocation)}
                      onMouseEnter={() => setPreviewValue(pickupLocation)}
                      onFocus={() => setPreviewValue(pickupLocation)}
                      className={cn(
                        "flex w-full cursor-pointer items-center gap-3 px-2 py-2.5 text-left text-sm transition-colors hover:bg-gray-100",
                        (previewValue || value) === pickupLocation &&
                          "bg-gray-100",
                      )}
                    >
                      <LucideMapPinPlus size={20}/>
                      <span className="font-medium text-gray-900">
                        {getLocationLabel(pickupLocation)}
                      </span>
                    </button>
                  </div>
                ) : null}
              </>
            ) : null}

            <div
              className={cn(
                "py-6",
                mode === "return" && "border-t border-gray-100",
              )}
            >
              <p className="pl-4 pb-2 text-xs font-bold text-gray-800">
                Branches
              </p>
              <ul>
                {locations.map((location) => (
                  <li key={location.value}>
                    <button
                      type="button"
                      onClick={() => handleSelect(location.value)}
                      onMouseEnter={() => setPreviewValue(location.value)}
                      onFocus={() => setPreviewValue(location.value)}
                      className={cn(
                        "flex w-full cursor-pointer items-center gap-1 px-4 py-4 text-left text-md font-medium transition-colors hover:bg-gray-100",
                        (previewValue || value) === location.value &&
                          "bg-gray-100",
                      )}
                    >
                      <LucideMapPinCheck size={20}/>
                      <span className="text-gray-900">{location.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="min-w-0 flex-1 overflow-y-auto p-5 bg-gray-100">
            {detail && displayLabel ? (
              <BranchLocationDetails label={displayLabel} detail={detail} />
            ) : (
              <p className="text-sm text-gray-500">{emptyDetailMessage}</p>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function BranchLocationDetails({
  label,
  detail,
}: {
  label: string;
  detail: BookingLocationDetail;
}) {
  return (
    <div>
      <div className="flex items-start gap-2">
        {/* <i
          className="bi bi-geo-alt mt-0.5 text-2xl text-gray-800"
          aria-hidden="true"
        /> */}
        <LucideMapPin size={24}/>
        <div>
          <p className="text-xl font-bold text-gray-900">{label}</p>
          <p className="mt-0.5 text-sm text-gray-500">{detail.address}</p>
          {detail.tel ? (
            <p className="mt-1 text-sm text-gray-500">{detail.tel}</p>
          ) : null}
          {detail.email ? (
            <p className="mt-1 text-sm text-gray-500">{detail.email}</p>
          ) : null}
        </div>
      </div>
      <div className="mt-6">
        <p className="text-sm font-semibold text-gray-900">Opening hours</p>
        <dl className="mt-2 space-y-1">
          {detail.hours.map((row) => (
            <div
              key={`${row.days}-${row.time}`}
              className="flex gap-4 text-sm text-gray-600"
            >
              <dt className="w-24 shrink-0">{row.days}</dt>
              <dd>{row.time}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}
